import { FunctionComponent } from "react";

import { Checkbox } from "@/components/Checkbox/Checkbox";
import {
	AutoCompleteItem as DropdownAutoCompleteItem,
	Dropdown,
} from "@/components/Dropdown/Dropdown";
import { useSnackBar } from "@/utils/useSnackbar";

import {
	useGetDynamicsCompanySyncTriggerQuery,
	useGetDynamicsConversationSyncQuery,
	useGetDynamicsDealCreationTriggerQuery,
	useGetDynamicsMarketingConfigQuery,
	useUpdateDynamicsCompanySyncTriggerMutation,
	useUpdateDynamicsConversationSyncMutation,
	useUpdateDynamicsDealCreationTriggerMutation,
	useUpdateDynamicsMarketingConfigMutation,
} from "@/pages/Private/redux/dynamics/dynamics.api";

import { MsdynamicsDealCreationTrigger, MsdynamicsSyncTrigger } from "../schema/dynamics";
import { DynamicsResyncSection } from "./DynamicsResyncSection";

type Ts = (key: string) => string;

interface DynamicsSyncSettingsTabProps {
	isConnected: boolean;
	ts: Ts;
}

/**
 * The account-wide sync settings: when prospects sync, when an opportunity (or lead) is
 * created after a reply, whether conversations are mirrored as annotations, and the manual
 * resync.
 *
 * The two marketing switches (a Dataverse marketing list per campaign, a Dataverse
 * campaign per list) live here as well — `msdynamics_marketing_config` is keyed by
 * `companyId`, so they apply to every list of the account.
 *
 * The opportunity-creation trigger is rendered **here only** — `msdynamics_deal_creation_trigger`
 * is keyed by `companyId` alone, so unlike HubSpot there is no per-list counterpart.
 */
export const DynamicsSyncSettingsTab: FunctionComponent<DynamicsSyncSettingsTabProps> = ({
	isConnected,
	ts,
}) => {
	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();

	const { data: companySyncTrigger } = useGetDynamicsCompanySyncTriggerQuery(undefined, {
		skip: !isConnected,
	});
	const [updateCompanySyncTrigger] = useUpdateDynamicsCompanySyncTriggerMutation();

	const { data: dealCreationTrigger } = useGetDynamicsDealCreationTriggerQuery(undefined, {
		skip: !isConnected,
	});
	const [updateDealCreationTrigger] = useUpdateDynamicsDealCreationTriggerMutation();

	const { data: conversationSync } = useGetDynamicsConversationSyncQuery(undefined, {
		skip: !isConnected,
	});
	const [updateConversationSync] = useUpdateDynamicsConversationSyncMutation();

	const {
		data: marketingConfig,
		isSuccess: marketingConfigLoaded,
		isFetching: marketingConfigFetching,
	} = useGetDynamicsMarketingConfigQuery(undefined, { skip: !isConnected });
	const [updateMarketingConfig, { isLoading: marketingConfigSaving }] =
		useUpdateDynamicsMarketingConfigMutation();
	/**
	 * The account-note save carries the stored marketing switches over (the API requires the two
	 * campaign switches), so it waits for a successful read with no save or refetch in flight: sent
	 * from a pending, failed or stale read it would switch a company's campaigns off (PREC-4751).
	 */
	const accountNotesSavable =
		marketingConfigLoaded && !marketingConfigFetching && !marketingConfigSaving;

	const syncTriggerOptions: DropdownAutoCompleteItem[] = [
		{ id: MsdynamicsSyncTrigger.DISABLED, title: ts("syncTrigger.DISABLED") },
		{ id: MsdynamicsSyncTrigger.ON_CAMPAIGN_START, title: ts("syncTrigger.ON_CAMPAIGN_START") },
		{ id: MsdynamicsSyncTrigger.ON_REPLY, title: ts("syncTrigger.ON_REPLY") },
	];

	const dealCreationTriggerOptions: DropdownAutoCompleteItem[] = [
		{ id: MsdynamicsDealCreationTrigger.DISABLED, title: ts("dealCreationTrigger.DISABLED") },
		{ id: MsdynamicsDealCreationTrigger.ON_RESPONSE, title: ts("dealCreationTrigger.ON_RESPONSE") },
		{
			id: MsdynamicsDealCreationTrigger.ON_POSITIVE_RESPONSE,
			title: ts("dealCreationTrigger.ON_POSITIVE_RESPONSE"),
		},
	];

	const handleSyncTriggerChange = async (value: DropdownAutoCompleteItem) => {
		try {
			await updateCompanySyncTrigger({ trigger: value.id as MsdynamicsSyncTrigger }).unwrap();
		} catch {
			handleOpenSnackbar(ts("syncTrigger.error"));
		}
	};

	const handleDealCreationTriggerChange = async (value: DropdownAutoCompleteItem) => {
		try {
			await updateDealCreationTrigger({
				trigger: value.id as MsdynamicsDealCreationTrigger,
				createAsLead: dealCreationTrigger?.createAsLead ?? false,
			}).unwrap();
		} catch {
			handleOpenSnackbar(ts("dealCreationTrigger.error"));
		}
	};

	const handleCreateAsLeadToggle = async () => {
		try {
			await updateDealCreationTrigger({
				trigger: dealCreationTrigger?.trigger ?? MsdynamicsDealCreationTrigger.DISABLED,
				createAsLead: !(dealCreationTrigger?.createAsLead ?? false),
			}).unwrap();
		} catch {
			handleOpenSnackbar(ts("dealCreationTrigger.error"));
		}
	};

	const handleConversationSyncToggle = async () => {
		try {
			await updateConversationSync({ enabled: !(conversationSync?.enabled ?? false) }).unwrap();
			handleOpenSnackbar(ts("conversationSync.success"));
		} catch {
			handleOpenSnackbar(ts("conversationSync.error"));
		}
	};

	/** The three marketing switches as shown, carried over by every marketing-config save. */
	const marketingSwitches = () => ({
		autoCreateMarketingLists: marketingConfig?.autoCreateMarketingLists ?? false,
		autoCreateCampaigns: marketingConfig?.autoCreateCampaigns ?? false,
		syncCampaignResponses: marketingConfig?.syncCampaignResponses ?? false,
	});

	/**
	 * All three marketing switches are sent on every save, so the ones that were not clicked are
	 * carried over. The API switches campaign responses off with the campaigns (PREC-4367). The
	 * account-note switch is left out: the API keeps it as stored.
	 */
	const handleMarketingConfigToggle = async (
		field: "autoCreateMarketingLists" | "autoCreateCampaigns" | "syncCampaignResponses"
	) => {
		const current = marketingSwitches();

		try {
			await updateMarketingConfig({ ...current, [field]: !current[field] }).unwrap();
			handleOpenSnackbar(ts("marketing.success"));
		} catch {
			handleOpenSnackbar(ts("marketing.error"));
		}
	};

	/** Saves the account-note switch (PREC-4751) — only this checkbox ever sends it. */
	const handleAccountNotesToggle = async () => {
		if (!accountNotesSavable) {
			return;
		}

		try {
			await updateMarketingConfig({
				...marketingSwitches(),
				attachConversationNotesToAccount: !(
					marketingConfig?.attachConversationNotesToAccount ?? false
				),
			}).unwrap();
			handleOpenSnackbar(ts("conversationSync.success"));
		} catch {
			handleOpenSnackbar(ts("conversationSync.error"));
		}
	};

	return (
		<div className="flex flex-col w-full">
			{renderMessageSnackbar()}
			<div className="text-sm text-text-meta mb-3">{ts("syncTrigger.description")}</div>
			<div className="w-full max-w-[400px]">
				<Dropdown
					data={syncTriggerOptions}
					defaultValue={
						companySyncTrigger
							? {
									id: companySyncTrigger.trigger,
									title: ts(`syncTrigger.${companySyncTrigger.trigger}`),
								}
							: undefined
					}
					floating={true}
					handleSelect={handleSyncTriggerChange}
					label={ts("syncTrigger.title")}
				/>
			</div>

			<div className="text-sm text-text-meta mb-3 mt-6">
				{ts("dealCreationTrigger.description")}
			</div>
			<div className="w-full max-w-[400px]" data-testid="dynamics-deal-creation-trigger">
				<Dropdown
					data={dealCreationTriggerOptions}
					defaultValue={
						dealCreationTrigger
							? {
									id: dealCreationTrigger.trigger,
									title: ts(`dealCreationTrigger.${dealCreationTrigger.trigger}`),
								}
							: undefined
					}
					floating={true}
					handleSelect={handleDealCreationTriggerChange}
					label={ts("dealCreationTrigger.title")}
				/>
			</div>
			<div className="mt-4">
				<Checkbox
					isChecked={dealCreationTrigger?.createAsLead ?? false}
					name="createAsLead"
					onChange={handleCreateAsLeadToggle}
				>
					{ts("dealCreationTrigger.createAsLead")}
				</Checkbox>
				<div className="text-sm text-text-meta mt-1">
					{ts("dealCreationTrigger.createAsLeadDescription")}
				</div>
			</div>

			<div className="mt-6 pt-6 border-t border-border-light">
				<div className="text-sm font-medium text-text mb-2">{ts("conversationSync.title")}</div>
				<Checkbox
					isChecked={conversationSync?.enabled ?? false}
					name="conversationSync"
					onChange={handleConversationSyncToggle}
				>
					{ts("conversationSync.label")}
				</Checkbox>
				<div className="text-sm text-text-meta mt-2 mb-3">{ts("conversationSync.description")}</div>
				<Checkbox
					isChecked={marketingConfig?.attachConversationNotesToAccount ?? false}
					isDisabled={!conversationSync?.enabled || !accountNotesSavable}
					name="attachConversationNotesToAccount"
					onChange={handleAccountNotesToggle}
				>
					{ts("conversationSync.accountNotes")}
				</Checkbox>
				<div className="text-sm text-text-meta mt-1">
					{ts(
						conversationSync?.enabled
							? "conversationSync.accountNotesDescription"
							: "conversationSync.accountNotesNeedSync"
					)}
				</div>
			</div>

			<div className="mt-6 pt-6 border-t border-border-light">
				<div className="text-sm font-medium text-text mb-2">{ts("marketing.title")}</div>
				<div className="text-sm text-text-meta mb-3">{ts("marketing.description")}</div>
				<Checkbox
					isChecked={marketingConfig?.autoCreateMarketingLists ?? false}
					name="autoCreateMarketingLists"
					onChange={() => handleMarketingConfigToggle("autoCreateMarketingLists")}
				>
					{ts("marketing.marketingLists")}
				</Checkbox>
				<div className="text-sm text-text-meta mt-1 mb-3">
					{ts("marketing.marketingListsDescription")}
				</div>
				<Checkbox
					isChecked={marketingConfig?.autoCreateCampaigns ?? false}
					name="autoCreateCampaigns"
					onChange={() => handleMarketingConfigToggle("autoCreateCampaigns")}
				>
					{ts("marketing.campaigns")}
				</Checkbox>
				<div className="text-sm text-text-meta mt-1 mb-3">
					{ts("marketing.campaignsDescription")}
				</div>
				<Checkbox
					isChecked={marketingConfig?.syncCampaignResponses ?? false}
					isDisabled={!marketingConfig?.autoCreateCampaigns}
					name="syncCampaignResponses"
					onChange={() => handleMarketingConfigToggle("syncCampaignResponses")}
				>
					{ts("marketing.campaignResponses")}
				</Checkbox>
				<div className="text-sm text-text-meta mt-1">
					{ts(
						marketingConfig?.autoCreateCampaigns
							? "marketing.campaignResponsesDescription"
							: "marketing.campaignResponsesNeedCampaigns"
					)}
				</div>
			</div>

			<DynamicsResyncSection />
		</div>
	);
};
